import React, { useState, useEffect } from 'react';
import { X, GitCompareArrows } from 'lucide-react';
import { getAppVersion, getIssueTypes, getProcessModelTitle } from '@process-advisor/process-miner/processMiner/processMiner';
import { openProgressModal } from '@process-advisor/process-miner/api/methods/modals/openProgressModal';
import { closeProgressModal } from '@process-advisor/process-miner/api/methods/modals/closeProgressModal';
import { updateProgressModal } from '@process-advisor/process-miner/api/methods/modals/updateProgressModal';
import { apiProvider } from "@process-advisor/process-miner/api/apiProvider";
import { DataGrid } from '@process-advisor/process-miner/dashboard/transitions/dataGrid';
import EvidenceHeader from '@process-advisor/process-miner/dashboard/shared/EvidenceHeader';
import { stepGridColumns } from '@process-advisor/process-miner/dashboard/transitions/stepGridColumns';

let forgeModule = null;
const loadForgeModule = async () => {
  if (!forgeModule) forgeModule = await import('@forge/bridge');
  return forgeModule;
};

const SLEEP_DELAY_MS = 50;

const outputJSONFormat_Insights = 
{
  "$schema": "https://json-schema.org/draft/2020-12/schema",
  "$id": "https://example.com/schemas/incident-workflow-analysis.response.schema.json",
  "title": "TransitionInsights",
  "type": "object",
  "additionalProperties": false,
  "required": [
    "key_discovery_rules",
    "likely_root_causes",
    "process_optimization_recommendations",
    "data_driven_nudges",
    "behavioral_adjustments",
    "summary_of_key_gains"
  ],
  "properties": {
    "key_discovery_rules": {
      "type": "object",
      "additionalProperties": false,
      "required": ["signals", "evidence", "interpretation"],
      "properties": {
        "signals": {
          "type": "array",
          "items": { "type": "string" }
        },
        "evidence": {
          "type": "array",
          "items": {
            "type": "object",
            "additionalProperties": false,
            "required": ["pattern", "evidence", "impact"],
            "properties": {
              "pattern": { "type": "string" },
              "evidence": { "type": "string" },
              "impact": { "type": "string" }
            }
          }
        },
        "interpretation": { "type": "string" }
      }
    },

    "likely_root_causes": {
      "type": "array",
      "items": {
        "type": "object",
        "additionalProperties": false,
        "required": ["symptom", "likely_cause"],
        "properties": {
          "symptom": { "type": "string" },
          "likely_cause": { "type": "string" }
        }
      }
    },

    "process_optimization_recommendations": {
      "type": "array",
      "items": {
        "type": "object",
        "additionalProperties": false,
        "required": ["recommendation", "change_type", "expected_effect"],
        "properties": {
          "recommendation": { "type": "string" },
          "change_type": {
            "type": "string",
            "enum": ["Workflow rule", "Workflow design", "Automation", "Data discipline", "Team practice"]
          },
          "expected_effect": { "type": "string" }
        }
      }
    },

    "data_driven_nudges": {
      "type": "array",
      "items": {
        "type": "object",
        "additionalProperties": false,
        "required": ["trigger", "nudge", "goal"],
        "properties": {
          "trigger": { "type": "string" },
          "nudge": { "type": "string" },
          "goal": { "type": "string" }
        }
      }
    },

    "behavioral_adjustments": {
      "type": "array",
      "items": {
        "type": "object",
        "additionalProperties": false,
        "required": ["practice_change", "rationale"],
        "properties": {
          "practice_change": { "type": "string" },
          "rationale": { "type": "string" }
        }
      }
    },

    "summary_of_key_gains": {
      "type": "array",
      "items": {
        "type": "object",
        "additionalProperties": false,
        "required": ["outcome", "expected_impact"],
        "properties": {
          "outcome": { "type": "string" },
          "expected_impact": { "type": "string" }
        }
      }
    }
  }
}


const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const replaceQuotes = (obj) => {
  for (const key in obj) {
    if (typeof obj[key] === 'string') {
      obj[key] = obj[key].replace(/"([^"]+)"/g, "'$1'");
    } else if (obj[key] && typeof obj[key] === 'object') {
      replaceQuotes(obj[key]);
    }
  }
};

// Helper function to render dashboard HTML (used for both display and export)
const renderDashboardHTML = (data) => {
  let html = '';

  // Key Discovery Rules Section
  if (data.key_discovery_rules) {
    html += `
      <div class="section">
        <div class="section-header">
          <div class="section-icon">🎯</div>
          <div class="section-title">Key Discovery Rules</div>
        </div>
        ${data.key_discovery_rules.signals && data.key_discovery_rules.signals.length > 0 ? 
          data.key_discovery_rules.signals.map((signal, index) => `
            <div class="discoveryRule-pair">
              <div class="signal-header">
                <span class="signal-number">${index + 1}</span>
                <span class="signal-text">${signal}</span>
              </div>
              ${data.key_discovery_rules.evidence && data.key_discovery_rules.evidence[index] ? `
                <div class="evidence-card">
                  <div class="evidence-header">📌 ${data.key_discovery_rules.evidence[index].pattern}</div>
                  <div class="evidence-detail"><strong>Evidence:</strong> ${data.key_discovery_rules.evidence[index].evidence}</div>
                  <span class="impact-tag">Impact: ${data.key_discovery_rules.evidence[index].impact}</span>
                </div>
              ` : ''}
            </div>
          `).join('') : ''}
        ${data.key_discovery_rules.interpretation ? `
          <div class="interpretation-box">
            <p><strong>Interpretation:</strong> ${data.key_discovery_rules.interpretation}</p>
          </div>
        ` : ''}
      </div>
    `;
  }

  // Root Causes Section
  if (data.likely_root_causes && data.likely_root_causes.length > 0) {
    html += `
      <div class="section">
        <div class="section-header">
          <div class="section-icon">🔍</div>
          <div class="section-title">Likely Root Causes</div>
        </div>
        ${data.likely_root_causes.map(cause => `
          <div class="card">
            <div class="card-title">⚠️ ${cause.symptom}</div>
            <div class="card-content"><strong>Likely Cause:</strong> ${cause.likely_cause}</div>
          </div>
        `).join('')}
      </div>
    `;
  }

  // Recommendations Section
  if (data.process_optimization_recommendations && data.process_optimization_recommendations.length > 0) {
    html += `
      <div class="section">
        <div class="section-header">
          <div class="section-icon">💡</div>
          <div class="section-title">Process Optimization Recommendations</div>
        </div>
        ${data.process_optimization_recommendations.map(rec => {
          let badgeClass = 'badge-workflow';
          if (rec.change_type.toLowerCase().includes('automation')) {
            badgeClass = 'badge-automation';
          } else if (rec.change_type.toLowerCase().includes('practice')) {
            badgeClass = 'badge-practice';
          }
          
          return `
            <div class="card">
              <span class="badge ${badgeClass}">${rec.change_type}</span>
              <div class="card-title">${rec.recommendation}</div>
              <div class="card-content"><strong>Expected Effect:</strong> ${rec.expected_effect}</div>
            </div>
          `;
        }).join('')}
      </div>
    `;
  }

  // Data-Driven Nudges Section
  if (data.data_driven_nudges && data.data_driven_nudges.length > 0) {
    html += `
      <div class="section">
        <div class="section-header">
          <div class="section-icon">🔔</div>
          <div class="section-title">Data-Driven Nudges</div>
        </div>
        ${data.data_driven_nudges.map(nudge => `
          <div class="card">
            <div class="trigger-box">
              <div class="trigger-label">TRIGGER</div>
              <div class="trigger-text">${nudge.trigger}</div>
            </div>
            <div class="card-title">📬 ${nudge.nudge}</div>
            <div class="card-content"><strong>Goal:</strong> ${nudge.goal}</div>
          </div>
        `).join('')}
      </div>
    `;
  }

  // Behavioral Adjustments Section
  if (data.behavioral_adjustments && data.behavioral_adjustments.length > 0) {
    html += `
      <div class="section">
        <div class="section-header">
          <div class="section-icon">👥</div>
          <div class="section-title">Behavioral Adjustments</div>
        </div>
        ${data.behavioral_adjustments.map(adjustment => `
          <div class="card">
            <div class="card-title">🔄 ${adjustment.practice_change}</div>
            <div class="card-content"><strong>Rationale:</strong> ${adjustment.rationale}</div>
          </div>
        `).join('')}
      </div>
    `;
  }

  // Summary of Key Gains Section
  if (data.summary_of_key_gains && data.summary_of_key_gains.length > 0) {
    html += `
      <div class="section">
        <div class="section-header">
          <div class="section-icon">📈</div>
          <div class="section-title">Summary of Key Gains</div>
        </div>
        <div class="gains-grid">
          ${data.summary_of_key_gains.map(gain => `
            <div class="gain-card">
              <div class="gain-title">✨ ${gain.outcome}</div>
              <div class="gain-impact">${gain.expected_impact}</div>
            </div>
          `).join('')}
        </div>
      </div>
    `;
  }

  return html;
};

// Export modal (AI Insights only)
const ExportModal = ({ isOpen, onClose, data, modelTitle }) => {
  useEffect(() => {
    const onEsc = (e) => e.key === 'Escape' && isOpen && onClose();
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [isOpen, onClose]);

  const createHTMLContent = () => `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Process Optimization Dashboard - ${modelTitle}</title>
    <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif; background-color: #f4f5f7; color: #172B4D; line-height: 1.6; }
        .container { max-width: 1200px; margin: 0 auto; padding: 24px 32px; }
        .section { background: white; border-radius: 8px; padding: 24px; margin-bottom: 16px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
        .section-header { display: flex; align-items: center; margin-bottom: 16px; padding-bottom: 12px; border-bottom: 2px solid #DFE1E6; }
        .section-icon { width: 32px; height: 32px; background: linear-gradient(135deg, #0052CC 0%, #0065FF 100%); border-radius: 6px; display: flex; align-items: center; justify-content: center; margin-right: 12px; font-size: 18px; }
        .section-title { font-size: 20px; font-weight: 600; color: #172B4D; }
        .discovery_rule-pair { margin-bottom: 16px; }
        .discovery_rule-pair:last-child { margin-bottom: 0; }
        .signal-header { background: #DEEBFF; color: #0747A6; padding: 8px 16px; border-radius: 4px 4px 0 0; display: flex; align-items: center; font-weight: 600; font-size: 14px; line-height: 1.4; }
        .signal-number { background: #0052CC; color: white; border-radius: 50%; width: 24px; height: 24px; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 13px; margin-right: 8px; flex-shrink: 0; }
        .signal-text { flex: 1; }
        .card { background: #F4F5F7; border-left: 4px solid #0052CC; border-radius: 4px; padding: 16px; margin-bottom: 8px; }
        .card:last-child { margin-bottom: 0; }
        .card-title { font-weight: 600; color: #172B4D; margin-bottom: 8px; font-size: 14px; line-height: 1.4; }
        .card-content { font-size: 14px; color: #505F79; line-height: 1.5; }
        .badge { display: inline-block; padding: 4px 8px; border-radius: 3px; font-size: 11px; font-weight: 600; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.3px; }
        .badge-workflow { background: #E3FCEF; color: #006644; }
        .badge-automation { background: #DEEBFF; color: #0747A6; }
        .badge-practice { background: #FFF0B3; color: #974F0C; }
        .evidence-card { background: white; border: 1px solid #DFE1E6; border-radius: 0 0 4px 4px; border-top: none; padding: 16px; }
        .evidence-header { font-weight: 600; color: #0052CC; margin-bottom: 8px; font-size: 14px; line-height: 1.4; }
        .evidence-detail { font-size: 14px; color: #505F79; margin-bottom: 8px; line-height: 1.5; }
        .impact-tag { background: #FFEBE6; color: #BF2600; padding: 6px 10px; border-radius: 3px; font-size: 12px; font-weight: 600; display: inline-block; margin-top: 8px; line-height: 1.3; }
        .interpretation-box { background: #EAE6FF; border-left: 4px solid #6554C0; padding: 16px; border-radius: 4px; margin-top: 16px; }
        .interpretation-box p { color: #403294; font-size: 14px; line-height: 1.6; margin: 0; }
        .gains-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 16px; }
        .gain-card { background: linear-gradient(135deg, #00875A 0%, #36B37E 100%); color: white; padding: 16px; border-radius: 6px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
        .gain-title { font-weight: 600; font-size: 15px; margin-bottom: 8px; line-height: 1.4; }
        .gain-impact { font-size: 14px; opacity: 0.95; line-height: 1.5; }
        .trigger-box { background: #FFFAE6; border: 1px solid #FFC400; border-radius: 4px; padding: 8px; margin-bottom: 8px; }
        .trigger-label { font-weight: 600; color: #7A6A0F; font-size: 11px; margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.5px; }
        .trigger-text { color: #594600; font-size: 14px; line-height: 1.5; }
    </style>
</head>
<body>
    <div class="container">
        ${renderDashboardHTML(data)}
    </div>
</body>
</html>`;

  const downloadFile = (content, filename, type) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    onClose();
  };

  const exportAsHTML = () => {
    const sanitizedTitle = modelTitle.toLowerCase().replace(/\s+/g, '-');
    downloadFile(createHTMLContent(), `ai-insights-${sanitizedTitle}.html`, 'text/html');
  };

  const exportAsJSON = () => {
    const sanitizedTitle = modelTitle.toLowerCase().replace(/\s+/g, '-');
    downloadFile(JSON.stringify(data, null, 2), `ai-insights-${sanitizedTitle}.json`, 'application/json');
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">Export AI Insights</div>
          <button className="modal-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </div>
        <div className="modal-body">
          <div className="modal-description">Choose your preferred export format:</div>
          <div className="export-option" onClick={exportAsHTML}>
            <svg className="export-icon" viewBox="0 0 24 24" fill="#0052cc">
              <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
            </svg>
            <div className="export-content">
              <div className="export-option-title">Export as HTML</div>
              <div className="export-option-description">Generate a printable HTML document</div>
            </div>
          </div>
          <div className="export-option" onClick={exportAsJSON}>
            <svg className="export-icon" viewBox="0 0 24 24" fill="#ff991f">
              <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
            </svg>
            <div className="export-content">
              <div className="export-option-title">Export as JSON</div>
              <div className="export-option-description">Download raw data for further analysis</div>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="modal-cancel-button" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

let processingCtr = 0;

const TransitionInsights = ({AI, refreshKey, processLabel='Process'}) => {
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  const [modelTitle, setModelTitle] = useState('');
  const [transitions, setTransitions] = useState([]);
  const [graphData, setGraphData] = useState([]);
  const [columns, setColumns] = useState([]);

  useEffect(() => {
    setModelTitle(getProcessModelTitle());
    setColumns(stepGridColumns());
    const steps = apiProvider.processStepsTable();
    setTransitions(steps);

    const tempGraphData = steps.slice(0, 10).map((s) => ({
      step: `${s.from_activity} -> ${s.to_activity}`,
      work_items: s.tasks
    }));
    setGraphData(tempGraphData);
  }, [refreshKey]);

  return (
    <div style={{ backgroundColor: '#f8f9fa', padding: 0 }}>
      <style>{`
        .modal-overlay{position:fixed;inset:0;background:rgba(9,30,66,.54);display:flex;align-items:center;justify-content:center;z-index:1000}
        .modal{background:#fff;border-radius:3px;width:90%;max-width:440px;box-shadow:0 8px 16px rgba(0,0,0,.3)}
        .modal-header{padding:20px 24px;border-bottom:2px solid #dfe1e6;display:flex;justify-content:space-between;align-items:center}
        .modal-title{font-size:20px;font-weight:500;color:#172b4d}
        .modal-close{background:none;border:none;cursor:pointer;padding:4px;color:#5e6c84}
        .modal-close:hover{color:#172b4d}
        .modal-body{padding:24px}
        .modal-description{font-size:14px;color:#5e6c84;margin-bottom:20px}
        .export-option{border:2px solid #dfe1e6;border-radius:3px;padding:16px;margin-bottom:12px;cursor:pointer;transition:all .2s;display:flex;align-items:flex-start}
        .export-option:hover{border-color:#0052cc;background:#f4f5f7}
        .export-icon{width:40px;height:40px;margin-right:12px;flex-shrink:0}
        .export-content{flex:1}
        .export-option-title{font-size:14px;font-weight:600;color:#172b4d;margin-bottom:4px}
        .export-option-description{font-size:12px;color:#5e6c84}
        .modal-footer{padding:16px 24px;border-top:2px solid #dfe1e6;display:flex;justify-content:flex-end}
        .modal-cancel-button{background:none;border:none;color:#5e6c84;padding:8px 12px;border-radius:3px;font-size:14px;font-weight:500;cursor:pointer;transition:background .2s}
        .modal-cancel-button:hover{background:#ebecf0}
        .section{background:#fff;border-radius:8px;padding:24px;margin-bottom:16px;box-shadow:0 1px 3px rgba(0,0,0,.1)}
        .section-header{display:flex;align-items:center;margin-bottom:16px;padding-bottom:12px;border-bottom:2px solid #dfe1e6}
        .section-icon{width:32px;height:32px;background:linear-gradient(135deg,#0052cc 0%,#0065ff 100%);border-radius:6px;display:flex;align-items:center;justify-content:center;margin-right:12px;font-size:18px}
        .section-title{font-size:20px;font-weight:600;color:#172b4d}
        .discovery_rule-pair{margin-bottom:16px}
        .discovery_rule-pair:last-child{margin-bottom:0}
        .signal-header{background:#deebff;color:#0747a6;padding:8px 16px;border-radius:4px 4px 0 0;display:flex;align-items:center;font-weight:600;font-size:14px;line-height:1.4}
        .signal-number{background:#0052cc;color:#fff;border-radius:50%;width:24px;height:24px;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:13px;margin-right:8px;flex-shrink:0}
        .signal-text{flex:1}
        .card{background:#f4f5f7;border-left:4px solid #0052cc;border-radius:4px;padding:16px;margin-bottom:8px}
        .card:last-child{margin-bottom:0}
        .card-title{font-weight:600;color:#172b4d;margin-bottom:8px;font-size:14px;line-height:1.4}
        .card-content{font-size:14px;color:#505f79;line-height:1.5}
        .badge{display:inline-block;padding:4px 8px;border-radius:3px;font-size:11px;font-weight:600;margin-bottom:8px;text-transform:uppercase;letter-spacing:.3px}
        .badge-workflow{background:#e3fcef;color:#006644}
        .badge-automation{background:#deebff;color:#0747a6}
        .badge-practice{background:#fff0b3;color:#974f0c}
        .evidence-card{background:#fff;border:1px solid #dfe1e6;border-radius:0 0 4px 4px;border-top:none;padding:16px}
        .evidence-header{font-weight:600;color:#0052cc;margin-bottom:8px;font-size:14px;line-height:1.4}
        .evidence-detail{font-size:14px;color:#505f79;margin-bottom:8px;line-height:1.5}
        .impact-tag{background:#ffebe6;color:#bf2600;padding:6px 10px;border-radius:3px;font-size:12px;font-weight:600;display:inline-block;margin-top:8px;line-height:1.3}
        .interpretation-box{background:#eae6ff;border-left:4px solid #6554c0;padding:16px;border-radius:4px;margin-top:16px}
        .interpretation-box p{color:#403294;font-size:14px;line-height:1.6;margin:0}
        .gains-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(280px,1fr));gap:16px}
        .gain-card{background:linear-gradient(135deg,#00875a 0%,#36b37e 100%);color:#fff;padding:16px;border-radius:6px;box-shadow:0 2px 4px rgba(0,0,0,.1)}
        .gain-title{font-weight:600;font-size:15px;margin-bottom:8px;line-height:1.4}
        .gain-impact{font-size:14px;opacity:.95;line-height:1.5}
        .trigger-box{background:#fffae6;border:1px solid #ffc400;border-radius:4px;padding:8px;margin-bottom:8px}
        .trigger-label{font-weight:600;color:#7a6a0f;font-size:11px;margin-bottom:4px;text-transform:uppercase;letter-spacing:.5px}
        .trigger-text{color:#594600;font-size:14px;line-height:1.5}
      `}</style>

      <EvidenceHeader
        icon={<GitCompareArrows size={18} />}
        title={`${processLabel} Transitions`}
        subtitle="Review activity flow patterns, handoffs, loops, and rework pathways."
      />

      <DataGrid rows={transitions} graphData={graphData} columns={columns} />

      <ExportModal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} data={{}} modelTitle={modelTitle} />
    </div>
  );
};

export default TransitionInsights;